import React, { useMemo } from 'react';
import { SimpleMarkdownRenderer } from '@components/Markdown/SimpleMarkdownRenderer';
import { Gavel as RuleIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';

import type { ComplianceRuleDocument } from '../types';
import { extractRuleSection } from '../utils/markdownUtils';

interface ComplianceRulePreviewCardProps {
  rule: ComplianceRuleDocument;
  isGlobal?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
}

export const ComplianceRulePreviewCard: React.FC<ComplianceRulePreviewCardProps> = ({
  rule,
  isGlobal = false,
  isDisabled = false,
  onClick,
}) => {
  // Extract only the Rule section from the markdown
  const ruleSectionMarkdown = useMemo(() => {
    return extractRuleSection(rule.body_md || '');
  }, [rule.body_md]);

  return (
    <Card
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        opacity: isDisabled ? 0.6 : 1,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick
          ? {
              boxShadow: 1,
              borderColor: 'borderColors.brand',
            }
          : undefined,
      }}
      onClick={onClick}
    >
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <RuleIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.25, flexWrap: "wrap" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                lineHeight: 1.25,
                color: 'textColors.darkText',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {rule.title || rule.selector_text || 'Untitled Rule'}
            </Typography>
            {rule.state === 'deprecated' && (
              <Chip
                label="Deprecated"
                size="small"
                sx={{ height: 16, fontSize: '0.625rem' }}
                color="default"
                variant="outlined"
              />
            )}
            {isDisabled && (
              <Chip
                label="Disabled"
                size="small"
                sx={{ height: 16, fontSize: '0.625rem' }}
                color="default"
                variant="outlined"
              />
            )}
            {!isDisabled && isGlobal && (
              <Chip
                label="Global"
                size="small"
                sx={{ height: 16, fontSize: '0.625rem' }}
                color="secondary"
                variant="outlined"
              />
            )}
            {!isDisabled && !isGlobal && (
              <Chip
                label="Organization"
                size="small"
                sx={{ height: 16, fontSize: '0.625rem' }}
                color="primary"
                variant="outlined"
              />
            )}
            {rule.always_select && (
              <Chip
                label="Always Select"
                size="small"
                sx={{ height: 16, fontSize: '0.625rem' }}
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          {/* Display facets as chips */}
          {(rule.facets?.payer?.length || rule.facets?.cpt?.length || rule.facets?.provider_type?.length) && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mt: 0.25 }}>
              {rule.facets.payer?.map((payer) => (
                <Chip
                  key={payer}
                  label={payer}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
              {rule.facets.cpt?.map((cpt) => (
                <Chip
                  key={cpt}
                  label={`CPT ${cpt}`}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
              {rule.facets.provider_type?.map((providerType) => (
                <Chip
                  key={providerType}
                  label={providerType}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section - Only Rule section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', width: '100%' }}>
        <Stack spacing={1}>
          {ruleSectionMarkdown ? (
            <SimpleMarkdownRenderer markdown={ruleSectionMarkdown} />
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
              {rule.selector_text || 'No rule content available'}
            </Typography>
          )}
        </Stack>
      </CardContent>

      {/* Footer with Click to View Button */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          mt: 'auto',
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            fontSize: '0.75rem',
            py: 0.5,
            textTransform: 'none',
          }}
        >
          Click to view full rule
        </Button>
      </Box>
    </Card>
  );
};

